import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ReceiptText, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { CheckoutResponse } from '@/services/types';
import { Button, Overline } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';
import { ReceiptViewer } from '@/features/transactions/ReceiptViewer';

export interface SaleCompleteViewProps {
    result: CheckoutResponse | 'queued';
    shopId: number;
    methodLabel?: string;
    cashReceived: number;
    change: number;
    onNewSale: () => void;
}

/** 08 — T5 result: full-bleed plum, the change (or total) is the hero, one next action. */
export const SaleCompleteView: React.FC<SaleCompleteViewProps> = ({ result, shopId, methodLabel, cashReceived, change, onNewSale }) => {
    const { t } = useTranslation();
    const [showReceipt, setShowReceipt] = useState(false);
    const queued = result === 'queued';
    const receiptNumber = queued ? null : result.receiptNumber;
    const total = queued ? null : result.grandTotal;
    const heroAmount = change > 0 ? change : total;

    const share = async () => {
        if (!receiptNumber) return;
        const text = `${receiptNumber} · ${total != null ? formatCurrency(total) : ''}`;
        if (navigator.share) { try { await navigator.share({ title: t('transactions.receipt'), text }); } catch { /* cancelled */ } }
        else { try { await navigator.clipboard.writeText(text); toast.success(t('checkout.linkCopied')); } catch { /* ignore */ } }
    };

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col bg-surface-brand text-white lg:flex-row">
            <div className="flex min-h-0 flex-1 flex-col items-center gap-6 overflow-y-auto px-5 pt-14 text-center lg:items-start lg:justify-center lg:px-[72px] lg:text-left">
                <div className="flex flex-col items-center gap-3.5 lg:flex-row lg:gap-5">
                    <span className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-full bg-status-success text-white shadow-[0_0_0_12px_rgba(24,160,107,.18)] lg:h-20 lg:w-20">
                        <Check className="h-11 w-11 lg:h-10 lg:w-10" strokeWidth={3} />
                    </span>
                    <div className="flex flex-col gap-1.5">
                        <h1 className="text-[32px] font-black leading-tight tracking-[-0.02em] lg:text-[38px]">{queued ? t('queue.saleQueued') : t('checkout.saleComplete')}</h1>
                        <p className="text-[15px] text-primary-100 lg:text-base">
                            {queued ? t('queue.saleQueuedHint') : [receiptNumber, methodLabel].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                </div>

                {!queued && heroAmount != null && (
                    <div className="flex w-full max-w-[560px] flex-col items-center gap-1 rounded-2xl bg-surface-card p-6 text-ink-primary lg:items-start lg:p-[32px_36px]">
                        <Overline tone="accent">{change > 0 ? t('checkout.change') : t('cart.total')}</Overline>
                        <span className="text-[48px] font-black leading-[1.05] tnum tracking-[-0.03em] text-ink-brand lg:text-[72px]">{formatCurrency(heroAmount)}</span>
                        {change > 0 && (
                            <div className="mt-2.5 flex w-full justify-between border-t border-line-subtle pt-3 text-sm text-ink-secondary lg:text-[15px]">
                                <span>{t('checkout.received')} <strong className="tnum text-ink-primary">{formatCurrency(cashReceived)}</strong></span>
                                <span>{t('cart.total')} <strong className="tnum text-ink-primary">{formatCurrency(total!)}</strong></span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div data-theme="dark" className="flex shrink-0 flex-col gap-2.5 px-5 pb-[max(28px,env(safe-area-inset-bottom))] pt-4 lg:w-[400px] lg:justify-end lg:bg-black/[.18] lg:p-8">
                {!queued && (
                    <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-1 lg:gap-3">
                        <Button variant="outline" size="md" fullWidth icon={<ReceiptText />} onClick={() => setShowReceipt(true)} className="lg:!h-14 lg:!text-lg">{t('checkout.viewReceipt')}</Button>
                        <Button variant="outline" size="md" fullWidth icon={<Share2 />} onClick={share} className="lg:!h-14 lg:!text-lg">{t('common.share')}</Button>
                    </div>
                )}
                <div className="hidden h-2 lg:block" />
                <Button variant="primary" size="lg" fullWidth onClick={onNewSale}>{t('checkout.newSale')}</Button>
            </div>

            {receiptNumber && <ReceiptViewer receiptNumber={receiptNumber} shopId={shopId} isOpen={showReceipt} onClose={() => setShowReceipt(false)} />}
        </div>
    );
};
export default SaleCompleteView;
